import React, {useEffect, useState} from "react";
import {motion} from "framer-motion";
import {BiLock, BiRefresh} from "react-icons/bi";
import {verifyCode, verifyPhoneAgain} from "../../api/cart";
import {useStateValue} from "../../context/StateProvider";
import CheckoutPhoneFooter from "./footer";

type SendMethod = 'sms' | 'call';

const Body = ({checkoutPaymentState}: { checkoutPaymentState: any }) => {
    const [{userCartInfo, cartItems, cartTotal, verificationCode}, dispatch] = useStateValue()

    const [code, setCode] = useState("");
    const [currentMethod, setCurrentMethod] = useState<SendMethod>('call');
    const [attemptCount, setAttemptCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string>("");

    useEffect(() => {
        setCode(verificationCode)
    }, [verificationCode, dispatch]);

    useEffect(() => {
        const savedMethod = localStorage.getItem('lastSendMethod') as SendMethod;
        if (savedMethod) {
            setCurrentMethod(savedMethod);
        } else {
            setCurrentMethod('call');
        }

        const savedAttempts = parseInt(localStorage.getItem('sendAttempts') || '0');
        setAttemptCount(savedAttempts);
    }, []);

    const [secondsLeft, setSecondsLeft] = useState(60);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setInterval(() => {
            setSecondsLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [secondsLeft]);

    const handleSendCode = async () => {
        if (isLoading) return;

        setIsLoading(true);
        setStatusMessage("");

        try {
            const nextMethod = currentMethod === 'sms' ? 'call' : 'sms';

            await verifyPhoneAgain({
                phone: userCartInfo.phone,
                type: nextMethod
            });

            setCurrentMethod(nextMethod);
            localStorage.setItem('lastSendMethod', nextMethod);

            const newAttempts = attemptCount + 1;
            setAttemptCount(newAttempts);
            localStorage.setItem('sendAttempts', newAttempts.toString());

            const methodName = nextMethod === 'sms' ? 'SMS' : 'звонок';
            setStatusMessage(`✅ Код отправлен через ${methodName}`);

            setSecondsLeft(60);
        } catch (error) {
            console.error('Error sending code:', error);
            setStatusMessage("❌ Ошибка отправки");
        } finally {
            setIsLoading(false);
            setTimeout(() => setStatusMessage(""), 3000);
        }
    };

    const getMethodLabel = (method: SendMethod) => {
        return method === 'sms' ? 'SMS' : 'звонок';
    };

    const getNextMethod = (): SendMethod => {
        return currentMethod === 'sms' ? 'call' : 'sms';
    };

    return (
        <div className="w-full h-full bg-cartBg flex flex-col p-4">
            {/* Статус сообщение */}
            {statusMessage && (
                <div className={`p-3 rounded-lg text-center text-sm mb-4 ${
                    statusMessage.includes('✅')
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                    {statusMessage}
                </div>
            )}

            {/* Поле ввода кода */}
            <div className="mb-4">
                <input
                    type="number"
                    className="form-control block w-full px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg focus:border-buttonColor focus:outline-none"
                    placeholder="Введите код"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                />
            </div>

            {/* Кнопка подтверждения */}
            <motion.button
                onClick={() => verifyCode({
                    code: code,
                    cartItems: cartItems,
                    userCartInfo: userCartInfo,
                    cartTotal: cartTotal
                }, verificationCode, checkoutPaymentState, dispatch)}
                whileTap={{scale: 0.95}}
                className="flex items-center justify-center gap-2 w-full p-3 rounded-full bg-gradient-to-tr from-buttonColor to-buttonColor hover:from-buttonColor hover:to-buttonColor transition-all duration-75 ease-in-out text-gray-50 text-lg mb-4 hover:shadow-lg"
            >
                <BiLock className="text-xl"/>
                Подтвердить код
            </motion.button>

            {/* Повторная отправка */}
            <div className="flex flex-col items-center space-y-3">
                {secondsLeft > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <BiRefresh className="text-xl animate-spin" />
                        <span>Повторная отправка через {secondsLeft} сек.</span>
                    </div>
                ) : (
                    <motion.button
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                        className="flex items-center justify-center gap-2 text-buttonColor hover:text-buttonColor-dark font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                        onClick={handleSendCode}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-buttonColor"></div>
                                <span>Отправка...</span>
                            </>
                        ) : (
                            <>
                                <BiRefresh className="text-2xl" />
                                <span>
                                    Отправить через {getMethodLabel(getNextMethod())}
                                </span>
                            </>
                        )}
                    </motion.button>
                )}
            </div>

            <div className="mt-4">
                <CheckoutPhoneFooter/>
            </div>
        </div>
    );
};

export default Body;